import { FilterField, FilterOperator, OfficeHoursMode, } from '../models/workflow-dtos.js';
import { OfficeHoursValidator, DEFAULT_WEEKLY_BUSINESS_HOURS, resolveTimezoneName, } from './office-hours.js';
// Backfilled into PROPERTY_SCOPE and removed from FilterField. Matched as
// literals so a row that escaped the backfill is still recognised.
const LEGACY_PROPERTY_SCOPE_FIELDS = new Set([
    'property.ids',
    'property.groupIds',
]);
function isLegacyPropertyScopeField(field) {
    return LEGACY_PROPERTY_SCOPE_FIELDS.has(field);
}
const WEEKDAYS = [
    'monday',
    'tuesday',
    'wednesday',
    'thursday',
    'friday',
    'saturday',
    'sunday',
];
function hasWeeklyScheduleShape(schedule) {
    if (!schedule || typeof schedule !== 'object') {
        return false;
    }
    return WEEKDAYS.some(day => day in schedule);
}
/**
 * Evaluate an office hours filter condition.
 * Returns true if the condition passes (item should be included).
 */
export function evaluateOfficeHoursCondition(condition, context) {
    const officeHoursValue = condition.value;
    if (officeHoursValue === OfficeHoursMode.ALWAYS) {
        return true;
    }
    const timezoneName = resolveTimezoneName(context.timezoneName);
    switch (officeHoursValue) {
        case OfficeHoursMode.CUSTOM_HOURS: {
            if (!hasWeeklyScheduleShape(context.customWeeklySchedule)) {
                return false;
            }
            return OfficeHoursValidator.isWithinBusinessHours(context.timestamp, timezoneName, context.customWeeklySchedule);
        }
        case OfficeHoursMode.BUSINESS_HOURS:
            return OfficeHoursValidator.isWithinBusinessHours(context.timestamp, timezoneName, hasWeeklyScheduleShape(context.weeklySchedule)
                ? context.weeklySchedule
                : DEFAULT_WEEKLY_BUSINESS_HOURS);
        case OfficeHoursMode.AFTER_HOURS:
            return !OfficeHoursValidator.isWithinBusinessHours(context.timestamp, timezoneName, hasWeeklyScheduleShape(context.weeklySchedule)
                ? context.weeklySchedule
                : DEFAULT_WEEKLY_BUSINESS_HOURS);
        default:
            return true;
    }
}
/**
 * Check if all filter conditions on an item pass.
 * Only evaluates OFFICE_HOURS conditions; unknown fields pass through.
 */
export function matchesOfficeHoursConditions(filterConditions, context) {
    if (!filterConditions?.length)
        return true;
    return filterConditions.every(condition => {
        if (condition.field === FilterField.OFFICE_HOURS) {
            return evaluateOfficeHoursCondition(condition, context);
        }
        return true;
    });
}
function isPropertyScopeValue(value) {
    return typeof value === 'object' && value !== null && !Array.isArray(value);
}
/**
 * Merge PROPERTY_SCOPE conditions into a single entry. The workflow builder
 * emits at most one, but POST/PUT /scenarios takes a raw condition array with
 * no count or duplicate-field constraint, so a stored list can carry several.
 * Other conditions pass through untouched. Every reader of stored conditions —
 * matcher, tiebreak, renderers, delete-rewrite — goes through this.
 */
export function normalizePropertyScope(filterConditions) {
    if (!filterConditions?.length)
        return [];
    if (!filterConditions.some(c => c.field === FilterField.PROPERTY_SCOPE)) {
        return filterConditions;
    }
    const propertyIds = new Set();
    const groupIds = new Set();
    const result = [];
    let scopeIndex = -1;
    for (const condition of filterConditions) {
        if (condition.field === FilterField.PROPERTY_SCOPE &&
            isPropertyScopeValue(condition.value)) {
            condition.value.propertyIds?.forEach(id => propertyIds.add(id));
            condition.value.groupIds?.forEach(id => groupIds.add(id));
            if (scopeIndex === -1)
                scopeIndex = result.length;
            continue;
        }
        result.push(condition);
    }
    if (propertyIds.size === 0 && groupIds.size === 0)
        return result;
    result.splice(scopeIndex, 0, {
        field: FilterField.PROPERTY_SCOPE,
        operator: FilterOperator.IN,
        value: {
            ...(propertyIds.size > 0 && { propertyIds: [...propertyIds] }),
            ...(groupIds.size > 0 && { groupIds: [...groupIds] }),
        },
    });
    return result;
}
/**
 * The property-scope predicate: unscoped (both empty) passes; otherwise the
 * caller must match ANY listed property OR ANY listed group. Scoped items
 * exclude callers with no property data at all (aligns with
 * filterWorkflowsByPropertyScope in langchain-agents).
 */
export function matchesPropertyScope(value, context) {
    if (!isPropertyScopeValue(value))
        return true;
    const propertyIds = value.propertyIds ?? [];
    const groupIds = value.groupIds ?? [];
    if (propertyIds.length === 0 && groupIds.length === 0)
        return true;
    const hitProperty = propertyIds.length > 0 &&
        !!context.callerPropertyIds?.some(id => propertyIds.includes(id));
    const hitGroup = groupIds.length > 0 &&
        !!context.callerPropertyGroupIds?.some(id => groupIds.includes(id));
    return hitProperty || hitGroup;
}
/**
 * Check if all filter conditions on an item pass.
 * Evaluates office hours, property scope, and group conditions.
 * Unknown fields pass through.
 *
 * Conditions AND across independent predicates; the property-scope predicate
 * owns its internal properties-OR-groups semantics.
 */
export function matchesFilterConditions(filterConditions, context) {
    if (!filterConditions?.length)
        return true;
    return normalizePropertyScope(filterConditions).every(condition => {
        // A row that escaped the backfill. Gate the rule off — an unrecognised
        // field would otherwise fall through to `default` and match every session.
        if (isLegacyPropertyScopeField(condition.field)) {
            console.warn(`[filter-conditions] legacy field "${condition.field}" — rule gated off`, context.ruleRef ?? {});
            return false;
        }
        switch (condition.field) {
            case FilterField.OFFICE_HOURS:
                return evaluateOfficeHoursCondition(condition, context);
            case FilterField.PROPERTY_SCOPE:
                return matchesPropertyScope(condition.value, context);
            case FilterField.GROUP_TYPES: {
                if (!context.callerGroupTypes?.length)
                    return true; // no caller data — pass through
                const requiredTypes = Array.isArray(condition.value)
                    ? condition.value
                    : [];
                if (requiredTypes.length === 0)
                    return true;
                return requiredTypes.some(type => context.callerGroupTypes.includes(type));
            }
            default:
                return true;
        }
    });
}
//# sourceMappingURL=filter-conditions.js.map